import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {finalize, Subject, takeUntil} from 'rxjs';
import {WalletService} from '../../services/wallet.service';
import {AuthService} from '../../services/auth.service';
import {Wallet} from '../../models/wallet';
import {NgForOf, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault} from '@angular/common';
import {AddPaymentMethodModalComponent} from './add-payment-method-modal/add-payment-method-modal.component';
import {User} from '../../models/user';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-payment-methods',
  imports: [
    NgForOf,
    NgSwitch,
    NgSwitchDefault,
    NgSwitchCase,
    NgIf
  ],
  templateUrl: './payment-methods.component.html',
  styleUrl: './payment-methods.component.scss'
})
export class PaymentMethodsComponent implements OnInit, OnDestroy {
  onDestroy$ = new Subject<boolean>();

  @Input() userId: string | null = null;
  @Input() companyId: string | null = null;

  loading = false

  // User
  currentUser: User | null = null

  // Wallets
  isWalletsLoading = false;
  isWalletsLoaded = false
  wallets: Wallet[] = []

  isWalletDefaultSubmitted = false;
  isWalletRemoveSubmitted = false;

  constructor(private modal: NgbModal,
              private authService: AuthService,
              private walletService: WalletService) {
  }

  ngOnInit() {
    this.authService.currentUser$.pipe().subscribe(currentUser => {
      if (currentUser) this.currentUser = currentUser
    })

    this.walletService.refresh$.pipe().subscribe(() => {
      this.refreshWallets()
    })

    if (this.userId == null && this.companyId == null) {
      throw Error("User ID or Company ID must be defined")
    }

    if (this.userId != null && this.companyId != null) {
      throw Error("Either User ID or Company ID but not both")
    }

    this.refreshWallets()
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  refreshWallets() {
    if (this.userId) {
      this.refreshUserWallets()
      return;
    }

    if (this.companyId) {
      this.refreshCompanyWallets()
      return;
    }
  }

  refreshUserWallets() {
    if (!this.userId) return;

    this.isWalletsLoading = true;
    this.walletService.findByUser().pipe(
      takeUntil(this.onDestroy$),
      finalize(() => {
        this.isWalletsLoading = false
        this.isWalletsLoaded = true
      }))
      .subscribe(wallets => {
        this.wallets = wallets;
      });
  }

  refreshCompanyWallets() {
    if (!this.companyId) return;

    this.isWalletsLoading = true;
    this.walletService.findByCompanyId(this.companyId).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => {
        this.isWalletsLoading = false
        this.isWalletsLoaded = true
      }))
      .subscribe(wallets => {
        this.wallets = wallets;
      });
  }

  setWalletDefault(wallet: Wallet) {
    this.isWalletDefaultSubmitted = true
    this.walletService.setDefault(wallet._id).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => this.isWalletDefaultSubmitted = false))
      .subscribe(() => {
        this.refreshWallets()
      });
  }

  removeWallet(wallet: Wallet) {
    this.isWalletRemoveSubmitted = true
    this.walletService.deactivate(wallet._id).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => this.isWalletRemoveSubmitted = false))
      .subscribe(() => {
        this.refreshWallets()
      });
  }

  openAddPaymentMethodModal() {
    if (!this.currentUser) return;

    const addPaymentMethodModal = this.modal.open(AddPaymentMethodModalComponent, {centered: true})
    addPaymentMethodModal.componentInstance.companyId = this.companyId
  }

}
